import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { coachStats, turnaroundMsText, type StatRow } from "@coaching/coachStats";
import { watchInbox, type Submission } from "../api";
import { useCoach, useCoachName } from "../auth";
import { Earnings, moneyRow } from "../components/Money";
import { useCoachingConfig } from "../components/PromiseNote";
import { ErrorState, Centred } from "../components/States";
import { SubmissionCard, useRespond } from "../components/SubmissionCard";
import { minutes, relativeMs } from "../format";

const statRow = (s: Submission): StatRow => ({
    status: s.status,
    submittedAt: s.timing.submittedAt.toMillis(),
    respondByAt: s.timing.respondByAt?.toMillis() ?? null,
    dueAt: s.timing.dueAt?.toMillis() ?? null,
    returnedAt: s.timing.returnedAt?.toMillis() ?? null,
    clipCount: s.clipCount,
    totalSec: s.totalSec,
});

const byMs = (pick: (s: Submission) => number | null) => (a: Submission, b: Submission) => (pick(a) ?? Infinity) - (pick(b) ?? Infinity);

/** The coach's first page: where they stand, then what needs them next. */
export default function Dashboard() {
    const { user, clubs } = useCoach();
    const name = useCoachName();
    const [rows, setRows] = useState<Submission[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [attempt, setAttempt] = useState(0);
    const { busyId, accepting, actionError, dialog, accept, decline } = useRespond();
    const cfg = useCoachingConfig();

    useEffect(() => {
        setError(null);
        return watchInbox(user.uid, setRows, (e) => setError(e.message));
    }, [user.uid, attempt]);

    const stats = useMemo(() => {
        const now = new Date();
        return coachStats((rows ?? []).map(statRow), now.getTime(), new Date(now.getFullYear(), now.getMonth(), 1).getTime());
    }, [rows]);
    const waiting = useMemo(() => (rows ?? []).filter((s) => s.status === "submitted").sort(byMs((s) => s.timing.respondByAt?.toMillis() ?? null)), [rows]);
    const active = useMemo(() => (rows ?? []).filter((s) => s.status === "accepted" || s.status === "in_review").sort(byMs((s) => s.timing.dueAt?.toMillis() ?? null)), [rows]);

    if (error) return <ErrorState title="Could not load your dashboard" body={error} retry={() => setAttempt((n) => n + 1)} />;
    if (rows === null) return <Centred spinner>Loading your dashboard</Centred>;

    const first = name.split(" ")[0];
    return (
        <div className="dashboard">
            <div className="page-head">
                <h1>{first ? `Hello, ${first}` : "Dashboard"}</h1>
                <p className="muted">{clubs.map((c) => c.clubName).join(" and ")}</p>
            </div>

            <div className="stats">
                <Stat to="/inbox" label="Waiting for you" value={stats.waiting} tone={stats.waiting > 0 ? "warn" : undefined}
                    note={stats.nextRespondBy !== null ? `Answer ${relativeMs(stats.nextRespondBy)}` : "Nothing to answer"} />
                <Stat to="/inbox?filter=in_progress" label="In progress" value={stats.inProgress} tone={stats.overdue > 0 ? "danger" : undefined}
                    note={stats.overdue > 0 ? `${stats.overdue} overdue` : stats.nextDue !== null ? `Next due ${relativeMs(stats.nextDue)}` : "None open"} />
                <Stat to="/inbox?filter=returned" label="Returned" value={stats.returned}
                    note={stats.returnedThisMonth === 1 ? "1 this month" : `${stats.returnedThisMonth} this month`} />
                <Stat label="On time" value={stats.onTimeRate === null ? "–" : `${Math.round(stats.onTimeRate * 100)}%`}
                    tone={stats.onTimeRate !== null && stats.onTimeRate < 0.9 ? "warn" : undefined}
                    note={stats.avgTurnaroundMs === null ? "No reviews returned yet" : `Average turnaround ${turnaroundMsText(stats.avgTurnaroundMs)}`} />
            </div>
            {stats.returned > 0 && (
                <p className="muted small dash-line">
                    {stats.clipsReviewed} {stats.clipsReviewed === 1 ? "clip" : "clips"} and {minutes(stats.footageSec)} of footage reviewed
                    {stats.declined > 0 ? ` · ${stats.declined} declined` : ""}{stats.expired > 0 ? ` · ${stats.expired} expired unanswered` : ""}
                </p>
            )}

            {actionError && <p className="error-text" role="alert">{actionError}</p>}

            {rows.length === 0 ? (
                <section className="card dash-empty">
                    <h2>No footage yet</h2>
                    <p className="muted">When a player at your club sends you clips they appear here and you get a notification. Your <Link to="/profile">profile</Link> is what they see when they choose a coach.</p>
                </section>
            ) : (
                <>
                    <Section title="Needs your answer" count={waiting.length} to="/inbox" empty="Nothing waiting. New requests land here first.">
                        {waiting.slice(0, 5).map((s) => <SubmissionCard key={s.id} s={s} busy={busyId === s.id} accepting={accepting === s.id} onAccept={() => accept(s)} onDecline={() => decline(s)} />)}
                    </Section>
                    <Section title="In progress" count={active.length} to="/inbox?filter=in_progress" empty="Nothing in progress.">
                        {active.slice(0, 5).map((s) => <SubmissionCard key={s.id} s={s} busy={busyId === s.id} accepting={accepting === s.id} onAccept={() => accept(s)} onDecline={() => decline(s)} />)}
                    </Section>
                </>
            )}
            {cfg && <Earnings rows={rows.map(moneyRow)} cfg={cfg} now={Date.now()} />}
            {dialog}
        </div>
    );
}

function Stat({ label, value, note, tone, to }: { label: string; value: number | string; note: string; tone?: "warn" | "danger"; to?: string }) {
    const body = (
        <>
            <span className="stat-label">{label}</span>
            <span className={`stat-value${tone ? ` ${tone}` : ""}`}>{value}</span>
            <span className="stat-note">{note}</span>
        </>
    );
    return to ? <Link className="card stat" to={to}>{body}</Link> : <div className="card stat">{body}</div>;
}

function Section({ title, count, to, empty, children }: { title: string; count: number; to: string; empty: string; children: React.ReactNode }) {
    return (
        <section className="dash-section">
            <div className="dash-section-head">
                <h2>{title} <span className="muted">{count}</span></h2>
                {count > 5 && <Link to={to} className="small">See all {count}</Link>}
            </div>
            {count === 0 ? <p className="muted small">{empty}</p> : <ul className="cards">{children}</ul>}
        </section>
    );
}
